// @flow

import React from 'react';
import type { Node } from 'react';
import { Link } from '@riseart/common';
import { CardEditorial } from '@riseart/card';
import { event as ENUM_EVENT } from 'Enum';
import { PicturePlaceholder } from 'shared_components/common/artdirection/picture/Picture';
import { EventType } from 'shared_components/common/event/Type';
import { EventDate } from 'shared_components/common/event/Date';
import { EventsRsvpProvider } from 'shared_data/providers/rsvp/Event';
import { EventRsvpButton } from 'shared_components/common/event/Rsvp';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';

type EventCardProps = {
  event: Object,
  layout?: 'horizontal' | 'vertical',
  size?: string,
  clickable?: boolean,
  lazyloadConfig?: Object,
  artDirectionSizeKey: string,
  additionalInfo?: Node,
  showOverlayContent?: boolean,
};

/**
 * EventCard
 *
 * @param {EventCardProps} props
 */
export const EventCard = ({
  clickable = true,
  event,
  layout = 'vertical',
  size = 'small',
  lazyloadConfig = {},
  artDirectionSizeKey,
  additionalInfo = null,
  showOverlayContent = true,
}: EventCardProps): Node => {
  const url = UrlAssembler.eventDetail(event.id, event.slug);
  const image =
    (event.images &&
      extractImageFileFromData.eventImage(event.images, ENUM_EVENT.image.type.TYPE_SQUARE)) ||
    null;
  const PictureComponent = image ? (
    <PicturePlaceholder
      type={artDirectionSizeKey}
      artDirectionKey="event.main"
      image={image}
      alt={event.name}
      lazyload={lazyloadConfig}
    />
  ) : null;

  return (
    <CardEditorial
      layout={layout}
      size={size}
      title={
        clickable ? (
          <Link href={url} title={event.name}>
            {event.name}
          </Link>
        ) : (
          event.name
        )
      }
      subTitle={<EventDate startDate={event.startDate} endDate={event.endDate} />}
      extra={event.type ? <EventType id={event.type} /> : null}
      {...(showOverlayContent && event.canRsvp
        ? {
            overlay: (
              <EventsRsvpProvider>
                <EventRsvpButton animated eventData={event} />
              </EventsRsvpProvider>
            ),
          }
        : null)}
      description={
        additionalInfo || event.venueAddress || event.venueCity ? (
          <React.Fragment>
            {[event.venueAddress, event.venueCity].filter((item) => !!item).join(', ')}
            {additionalInfo || null}
          </React.Fragment>
        ) : null
      }
    >
      {clickable ? (
        <Link href={url} title={event.name}>
          {PictureComponent}
        </Link>
      ) : (
        PictureComponent
      )}
    </CardEditorial>
  );
};
